import { useRef, useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { getCaptionValue } from '@pm2/content-engine';
import { ElementToolbar } from '@pm2/element-toolbar';
import { usePM2DisplayContext, pm2ConfigStore } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { useEntryKey, flashcardEntrySchema, UniversalLightboxCard, UniversalHarmonicaCard } from '@utils/component-framework';
import { useToggleState, SizeTransition } from '@utils/react';
import { FlashcardEmbed } from './comp.embed.js';
import { card, lightboxWrapper, wrapper, contentTransitionDurationMs, content } from './comp.router.style.js';
import { ScrambledCard } from './comp.scrambled.js';
import { jsx } from 'react/jsx-runtime';

function FlashcardRouter(props) {
  const rootRef = useRef(null);
  const modal = usePm2Modal();
  const {
    inGrid,
    inLightbox
  } = usePM2DisplayContext();
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const badgeImage = pm2ConfigStore.useSelector(state => state.config.theme.componentPreviewImage);
  const resolvedDisplayMode = inGrid ? "lightbox" : props.displayMode;
  const [expanded, expansionActions] = useToggleState();
  const [, onResetEntry] = useEntryKey({
    componentEnvelope: props.component.envelope,
    defaultEntryKey: props.component.entryKey,
    schema: flashcardEntrySchema,
    parentOnResetEntry: props.onResetEntry,
    onEntryKeyReset: useCallback(nextEntryKey => {
      props.node.component.entryKey = nextEntryKey;
    }, [props.node.component])
  });
  const state = (() => {
    if (props.isScrambled) {
      return "scrambled";
    }
    if (inLightbox) {
      return "lightbox-content";
    }
    if (!expanded) {
      return resolvedDisplayMode;
    }
    return "embed";
  })();
  const onLightboxActivate = useCallback(() => {
    modal.fromComponentFrameworkNode(/*#__PURE__*/jsx(FlashcardRouter, {
      ...props
    }), "component-framework.flashcard");
  }, [modal, props]);
  const onCollapse = useCallback(() => {
    expansionActions.disable();
  }, [expansionActions]);
  const content$1 = (() => {
    switch (state) {
      case "lightbox-content":
        {
          return /*#__PURE__*/jsx("div", {
            className: lightboxWrapper,
            children: /*#__PURE__*/jsx(FlashcardEmbed, {
              ...props,
              onResetEntry: onResetEntry
            })
          });
        }
      case "embed":
        return /*#__PURE__*/jsx(FlashcardEmbed, {
          ...props,
          onResetEntry: onResetEntry,
          onCollapse: resolvedDisplayMode === "harmonica" ? onCollapse : undefined
        });
      case "harmonica":
        return /*#__PURE__*/jsx(UniversalHarmonicaCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title),
          description: props.richtextDescription,
          audience: audience,
          badgeImageSrc: badgeImage,
          indexOrLevel: props.indexOrLevel,
          onClick: expansionActions.enable,
          children: /*#__PURE__*/jsx(UniversalHarmonicaCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "lightbox":
        return /*#__PURE__*/jsx(UniversalLightboxCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title),
          description: props.lightboxDescription,
          audience: audience,
          badgeImageSrc: badgeImage,
          previewImageSrc: props.previewImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onLightboxActivate,
          children: /*#__PURE__*/jsx(UniversalLightboxCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "scrambled":
        return /*#__PURE__*/jsx(ScrambledCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title) ?? ""
        });
    }
  })();
  return /*#__PURE__*/jsx("div", {
    ref: rootRef,
    className: wrapper,
    children: /*#__PURE__*/jsx(TransitionGroup, {
      component: null,
      children: /*#__PURE__*/jsx(SizeTransition, {
        axis: "vertical",
        swappable: true,
        classNames: content,
        timeout: contentTransitionDurationMs,
        delayMs: {
          enter: 10,
          exit: 0
        },
        onEnter: () => document.documentElement.style.setProperty("overflow-anchor", "none"),
        onEntered: () => document.documentElement.style.setProperty("overflow-anchor", ""),
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          "data-node-id": props.component.envelope.ID,
          className: content,
          children: content$1
        })
      }, state)
    })
  });
}

export { FlashcardRouter };
//# sourceMappingURL=comp.router.js.map
